import { useCallback, useEffect, useState } from "react";
import {
  CHECKOUT_STATUS,
  KeyCost,
  KeyInfo,
  PostCpCheckoutCompleteResponse,
  PostGenerateKeyRequest,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import useStatusMutation from "hook/useStatusMutation";
import {
  postCpCheckoutCompleteKey,
  postCpCheckoutKey,
  postMyDwGenerateKey,
} from "api/subscriptionAPI";
import { CustomError } from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useKeyType } from "hook/subscription/useKeyType";
import GenerateKeyModalPresenter from "./GenerateKeyModalPresenter";
import { notify } from "components/atoms/notification/Notification";
import { PRICE_REGEX } from "utils/regEx";
import { checkEmpty, toTrim } from "utils/functions";

type Props = {
  onModal: () => void;
  refetch: () => void;
};

export default function GenerateKeyModal(props: Props): JSX.Element {
  const { handleErrorNotify, intlString, lang } = useApiError();
  const { calculateTotalCost } = useKeyType();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  // const isMobile = useMediaQuery({ maxWidth: mediaQuery.mobileMax });
  const [page, setPage] = useState<number>(0);
  const [isSuccessKey, setIsSuccessKey] = useState<boolean>(false);
  const [successKeyList, setSuccessKeyList] = useState<string[]>([]);

  const [generateKeyData, setGenerateKeyData] = useState<KeyInfo>({
    keyType: undefined,
    duration: undefined,
    number: undefined,
    qty: undefined,
    reference: undefined,
    distributor: undefined,
    actualPrice: undefined,
    isFreeKey: undefined,
  });

  const [cost, setCost] = useState<KeyCost>({
    monthlyCost: 0,
    totalCost: 0,
  });

  const [validateMsg, setValidateMsg] = useState<{ [key: string]: string }>({
    generateKeyInfo: "",
    confirmKeyInfo: "",
  });

  const [generateValidateMsg, setGenerateValidateMsg] = useState<
    Record<string, string>
  >({
    duration: "",
    number: "",
    qty: "",
    actualPrice: "",
  });

  const [isDisabledNext, setIsDisabledNext] = useState<Record<string, boolean>>(
    { type: true, info: true }
  );

  const keyInfoToPayload = (keyInfo: KeyInfo): PostGenerateKeyRequest => {
    Object.keys(keyInfo).forEach((key) => {
      if (
        typeof keyInfo[key as keyof KeyInfo] === "string" &&
        !checkEmpty(keyInfo[key as keyof KeyInfo] as string)
      ) {
        keyInfo[key as keyof KeyInfo] = toTrim(
          keyInfo[key as keyof KeyInfo] as string
        ) as any;
      }
    });
    return {
      ...keyInfo,
      keyType: keyInfo.keyType || SUBSCRIPTION_TYPE.STANDARD,
      duration: keyInfo.duration || 1,
      qty: keyInfo.qty || 0,
      ...(keyInfo.keyType === SUBSCRIPTION_TYPE.STANDARD && {
        channels: keyInfo.number || 0,
      }),
      ...(keyInfo.keyType === SUBSCRIPTION_TYPE.STORAGE && {
        capacity: keyInfo.number || 0,
      }),
    };
  };

  const listener = (event: MessageEvent) => {
    // console.log(
    //   event,
    //   event.data,
    //   event.origin,
    //   window.location.origin,
    //   "event"
    // );
    if (event.origin !== window.location.origin) {
      return;
    }
    if (
      event.data?.type !== CHECKOUT_STATUS.SUCCESS &&
      event.data?.type !== CHECKOUT_STATUS.FAIL
    ) {
      return;
    }

    if (event.data.type === CHECKOUT_STATUS.SUCCESS) {
      console.log("✅ 결제 성공");
      mutationCpCheckoutCompleteKey.mutate({
        accountId: selectedAccount.accountId,
        payload: {
          ...keyInfoToPayload(generateKeyData),
          sessionId: event.data.sessionId,
        },
      });
    } else if (event.data.type === CHECKOUT_STATUS.FAIL) {
      console.log("❌ 결제 실패");
      notify(
        "error",
        `Checkout was not completed. \n
              Please check your information and try again.`
      );
      setValidateMsg((msg) => {
        return {
          ...msg,
          confirmKeyInfo: intlString(
            lang.label.subscription.generate.checkout.fail
          ),
        };
      });
    }
    window.removeEventListener("message", listener);
  };

  const onValidateGenerateKeyInfo = useCallback(() => {
    setGenerateValidateMsg({
      duration: "",
      number: "",
      qty: "",
    });
    if (!generateKeyData.duration) {
      setGenerateValidateMsg((val) => {
        return {
          ...val,
          duration: intlString(
            lang.label.subscription.duration.essential.validate
          ),
        };
      });
      return true;
    }
    if (!generateKeyData.number) {
      setGenerateValidateMsg((val) => {
        return {
          ...val,
          number: "Enter number of channels.",
        };
      });
      return true;
    }
    if (!generateKeyData.qty) {
      setGenerateValidateMsg((val) => {
        return {
          ...val,
          qty: "Enter number of keys to generate.",
        };
      });
      return true;
    }
    if (
      generateKeyData.actualPrice &&
      !PRICE_REGEX.test(String(generateKeyData.actualPrice)) &&
      !generateKeyData.isFreeKey
    ) {
      setGenerateValidateMsg((val) => {
        return {
          ...val,
          actualPrice: "Enter number of keys to generate.",
        };
      });
      return true;
    }
    return false;
  }, [generateValidateMsg, generateKeyData]);

  const mutationMyDwGenerateKey = useStatusMutation(postMyDwGenerateKey, {
    onSuccess: (res: any) => {
      if (res.error === 0 && res.result) {
        setIsSuccessKey(true);
        setSuccessKeyList(res.result);
        props.refetch();
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.generate.keys.fail);
    },
  });

  const mutationCpCheckoutCompleteKey = useStatusMutation(
    postCpCheckoutCompleteKey,
    {
      onSuccess: (res: PostCpCheckoutCompleteResponse) => {
        setIsSuccessKey(true);
        setSuccessKeyList(res.result);
        props.refetch();
      },
      onError: (err: CustomError) => {
        setValidateMsg((msg) => {
          return {
            ...msg,
            confirmKeyInfo: intlString(
              lang.label.subscription.generate.checkout.fail
            ),
          };
        });
        handleErrorNotify(err, lang.label.subscription.checkout.fail);
      },
    }
  );

  const mutationCpCheckoutKey = useStatusMutation(postCpCheckoutKey, {
    onSuccess: (res: any) => {
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;
      // console.log(res.result);

      // if (res.result !== undefined || res.result === "") {
      //   return;
      // }
      const testUrl = res.result;
      const testUrl2 =
        "http://localhost:3000/subscription/keys/checkout/success?session_id=cs_test_a1kV3ahG35JyWyp23UXn2EMYsEtQydXYFgklJb2XX5dS4MZbO0xGT5dp62";
      const testUrl3 = "http://localhost:3000/subscription/keys/checkout/fail";
      const testUrl4 =
        "http://192.168.10.161:3000/subscription/keys/checkout/success?session_id=cs_test_a1kV3ahG35JyWyp23UXn2EMYsEtQydXYFgklJb2XX5dS4MZbO0xGT5dp62";
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      window.addEventListener("message", listener);

      // console.log(isMobile);
      if (isMobile) {
        window.open(testUrl, "_blank");
      } else {
        window.open(
          testUrl,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.subscription.checkout.fail);
    },
  });

  const onMutateGenerateKey = () => {
    const payload: PostGenerateKeyRequest = keyInfoToPayload(generateKeyData);

    if (selectedAccount.accountLevel === LEVEL_TYPE.DW) {
      mutationMyDwGenerateKey.mutate({
        accountId: selectedAccount.accountId,
        payload,
      });
    } else {
      setValidateMsg((msg) => {
        return {
          ...msg,
          confirmKeyInfo: "",
        };
      });
      mutationCpCheckoutKey.mutate({
        accountId: selectedAccount.accountId,
        payload: {
          ...payload,
          ...cost,
        },
      });
    }
  };
  const onChangePage = (name: string, index?: number) => {
    // console.log(generateKeyData);
    const isValid = Object.entries(generateKeyData)
      .filter(
        ([key]) =>
          key !== "reference" &&
          key !== "distributor" &&
          key !== "actualPrice" &&
          key !== "isFreeKey"
      )
      .every(([_, value]) => value !== undefined);

    setValidateMsg((msg) => {
      return {
        ...msg,
        confirmKeyInfo: "",
      };
    });

    if (page === 0 && !generateKeyData.keyType) {
      return;
    }
    if (name === "next") {
      if (page === 1) {
        if (onValidateGenerateKeyInfo()) {
          return;
        }

        if (!isValid) {
          setValidateMsg((msg) => {
            return {
              ...msg,
              generateKeyInfo: intlString(
                lang.label.subscription.generate.essential.validate
              ),
            };
          });
          return;
        }
      }
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      props.onModal();
    }
    if (name === "step" && index !== undefined) {
      if (index === 2) {
        if (onValidateGenerateKeyInfo()) {
          return;
        }
        if (!isValid) {
          setValidateMsg((msg) => {
            return {
              ...msg,
              generateKeyInfo: intlString(
                lang.label.subscription.generate.essential.validate
              ),
            };
          });
          setPage(1);
          return;
        }
      }
      setPage(index);
    }
  };

  const onChangeKeyData = useCallback(
    (name: string, value: any) => {
      if (name === "duration" && value !== "") {
        setGenerateValidateMsg((val) => {
          return {
            ...val,
            duration: "",
          };
        });
      }
      if (name === "number" && value !== "") {
        setGenerateValidateMsg((val) => {
          return {
            ...val,
            number: "",
          };
        });
      }
      if (name === "qty" && value !== "") {
        setGenerateValidateMsg((val) => {
          return {
            ...val,
            qty: "",
          };
        });
      }
      if (name === "actualPrice" && value === "") {
        setGenerateValidateMsg((val) => {
          return {
            ...val,
            actualPrice: "",
          };
        });
      }
      setGenerateKeyData((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    },
    [setGenerateKeyData]
  );

  useEffect(() => {
    if (generateKeyData.keyType) {
      setIsDisabledNext((dis) => {
        return {
          ...dis,
          type: false,
        };
      });
    }
    if (page === 1) {
      if (
        generateKeyData.duration &&
        generateKeyData.number &&
        generateKeyData.qty
      ) {
        setIsDisabledNext((dis) => {
          return {
            ...dis,
            info: false,
          };
        });
      }
    }
    setCost(calculateTotalCost(generateKeyData));
  }, [page, generateKeyData]);

  return (
    <GenerateKeyModalPresenter
      generateKeyData={generateKeyData}
      cost={cost}
      page={page}
      successKeyList={successKeyList}
      isSuccessKey={isSuccessKey}
      validateMsg={validateMsg}
      isDisabledNext={isDisabledNext}
      generateValidateMsg={generateValidateMsg}
      onModal={props.onModal}
      onChangePage={onChangePage}
      onChangeKeyData={onChangeKeyData}
      onMutateGenerateKey={onMutateGenerateKey}
    />
  );
}
